import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import {
    getPkkRequirements,
    type PkkAgeGroup,
    type PkkResidence,
    type PkkLicenseType,
    type PkkRequirementsMessages,
    type PkkRequirementsResult,
} from './PkkRequirements.js';

interface PkkChecklistMessages {
    peselQuestion: string;
    withoutPesel: string;
    student: string;
    applicant: string;
    polish: string;
    foreign: string;
    ukr: string;
    residenceNotice: string;
    questions: string;
    age: string;
    under18: string;
    adult: string;
    applicationType: string;
    first: string;
    additional: string;
    medicalQuestion: string;
    photoQuestion: string;
    yes: string;
    no: string;
    result: string;
    have: string;
    missing: string;
    always: string;
    nextStep: string;
    reset: string;
    requirements: PkkRequirementsMessages;
}

/**
 * "Sprawdź, czego potrzebujesz do PKK" — a small, self-contained tool on
 * prawkostart.pl's PKK guide. Registers itself as <pkk-checklist></pkk-checklist>
 * the moment this module loads (standard custom-element upgrade), same as
 * every other Lit component in this repo — no manual mount call needed,
 * unlike the vanilla-class DrivingLicenseCostCalculator sibling in this same
 * directory.
 *
 * Privacy: answers live only in this instance's own @state fields, for the
 * lifetime of the page. Nothing is written to localStorage/cookies, nothing
 * is sent to a server or to analytics — a page reload clears everything.
 * Keep it that way; see the "PRYWATNOŚĆ MIKROAPLIKACJI" requirement this was
 * built against before adding any persistence or network call here.
 */
@customElement('pkk-checklist')
export class PkkChecklist extends LitElement {
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    @property({ type: Object }) messages?: PkkChecklistMessages;

    @state() private hasPesel: boolean | null = null;
    @state() private residence: PkkResidence | null = null;
    @state() private ageGroup: PkkAgeGroup | null = null;
    @state() private licenseType: PkkLicenseType | null = null;
    @state() private hasMedicalCertificate: boolean | null = null;
    @state() private hasPhoto: boolean | null = null;

    private get isComplete(): boolean {
        return (
            this.hasPesel !== null &&
            this.residence !== null &&
            this.ageGroup !== null &&
            this.licenseType !== null &&
            this.hasMedicalCertificate !== null &&
            this.hasPhoto !== null
        );
    }

    private get result(): PkkRequirementsResult | null {
        if (!this.isComplete || !this.messages) return null;

        return getPkkRequirements(
            {
                residence: this.residence!,
                ageGroup: this.ageGroup!,
                licenseType: this.licenseType!,
                hasMedicalCertificate: this.hasMedicalCertificate!,
                hasPhoto: this.hasPhoto!,
            },
            this.messages.requirements,
        );
    }

    private reset(): void {
        this.hasPesel = null;
        this.residence = null;
        this.ageGroup = null;
        this.licenseType = null;
        this.hasMedicalCertificate = null;
        this.hasPhoto = null;
    }

    render() {
        if (!this.messages) return nothing;
        const result = this.result;

        return html`
            <div class="pkk-checklist__form" role="group" aria-label=${this.messages.questions}>
                ${this.renderChoiceQuestion(
                    this.messages!.age,
                    this.ageGroup,
                    [
                        ['under18', this.messages!.under18],
                        ['18plus', this.messages!.adult],
                    ],
                    (value) => (this.ageGroup = value as PkkAgeGroup),
                )}
                ${this.renderChoiceQuestion(
                    this.messages!.applicationType,
                    this.licenseType,
                    [
                        ['first', this.messages!.first],
                        ['additional', this.messages!.additional],
                    ],
                    (value) => (this.licenseType = value as PkkLicenseType),
                )}
                ${this.renderYesNoQuestion(this.messages!.medicalQuestion, this.hasMedicalCertificate, (value) => (this.hasMedicalCertificate = value))}
                ${this.renderYesNoQuestion(this.messages!.photoQuestion, this.hasPhoto, (value) => (this.hasPhoto = value))}
                ${this.renderChoiceQuestion(
                    this.messages.applicant,
                    this.residence,
                    [
                        ['polish', this.messages.polish],
                        ['foreign', this.messages.foreign],
                        ['ukr', this.messages.ukr],
                        ['student', this.messages.student],
                    ],
                    (value) => (this.residence = value as PkkResidence),
                )}
                ${this.renderYesNoQuestion(this.messages.peselQuestion, this.hasPesel, (value) => (this.hasPesel = value))}
            </div>

            ${result ? this.renderResult(result) : nothing}
        `;
    }

    private renderChoiceQuestion(
        label: string,
        current: string | null,
        options: [string, string][],
        onChange: (value: string) => void,
    ) {
        return html`
            <fieldset class="pkk-checklist__question">
                <legend>${label}</legend>
                <div class="pkk-checklist__options">
                    ${options.map(
                        ([value, optionLabel]) => html`
                            <button
                                type="button"
                                class="pkk-checklist__option ${current === value ? 'pkk-checklist__option--selected' : ''}"
                                aria-pressed=${current === value}
                                @click=${() => onChange(value)}
                            >
                                ${optionLabel}
                            </button>
                        `,
                    )}
                </div>
            </fieldset>
        `;
    }

    private renderYesNoQuestion(label: string, current: boolean | null, onChange: (value: boolean) => void) {
        return this.renderChoiceQuestion(
            label,
            current === null ? null : current ? 'yes' : 'no',
            [
                ['yes', this.messages!.yes],
                ['no', this.messages!.no],
            ],
            (value) => onChange(value === 'yes'),
        );
    }

    private renderResult(result: PkkRequirementsResult) {
        return html`
            <div class="pkk-checklist__result" role="status">
                <h3>${this.messages!.result}</h3>

                <ul class="pkk-checklist__items">
                    ${result.items.map(
                        (item) => html`
                            <li class="pkk-checklist__item pkk-checklist__item--${item.status}">
                                <span class="pkk-checklist__icon" aria-hidden="true">
                                    ${item.status === 'missing' ? '✗' : item.status === 'have' ? '✓' : '•'}
                                </span>
                                <span><strong>${item.status === 'have' ? this.messages!.have : item.status === 'missing' ? this.messages!.missing : this.messages!.always}</strong> ${item.label}</span>
                            </li>
                        `,
                    )}
                </ul>

                <p class="pkk-checklist__next-step">
                    <strong>${this.messages!.nextStep}</strong> ${result.nextStep}
                </p>

                ${this.hasPesel === false ? html`<p>${this.messages!.withoutPesel}</p>` : nothing}
                <p>${this.messages!.residenceNotice}</p>

                <button type="button" class="pkk-checklist__reset" @click=${() => this.reset()}>
                    ${this.messages!.reset}
                </button>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'pkk-checklist': PkkChecklist;
    }
}
